'use client';

import { c as _c } from "react/compiler-runtime";
import classNames from 'classnames';
import { useInterval } from 'design-system/client';
import Image from 'next/image';
import type { StaticImageData } from 'next/image';
import { useLocale } from 'next-intl';
import { useCallback, useMemo, useState } from 'react';
import StreambotWrapper, { type StreambotWrapperProps } from '../StreambotWrapper';
import type { StreambotComponentProps, WidgetAlignment } from '../types';
import getDefaultValues from './getDefaultValues';
import betterplaceLogo from './images/betterplace-logo.png';
import betterplayLogo from './images/betterplay-logo.png';
import styles from './ProjectLogos.module.css';
import { type ProjectLogosWidgetSettings } from './projectLogosWidgetSettingsSchema';
import { useApiV4WithDemoData } from '@/api';
export type ProjectLogosData = Array<string | StaticImageData>;
const defaultDemoData = [betterplaceLogo, betterplayLogo] satisfies ProjectLogosData;
export type ProjectLogosProps = StreambotComponentProps<Omit<ProjectLogosWidgetSettings, 'darkMode'>, ProjectLogosData>;
type ProjectLogoContentProps = Required<Omit<ProjectLogosProps, Exclude<keyof StreambotWrapperProps, 'textAlign'>>>;
function useData(fundraisingEventId, demoData) {
  const $ = _c(3);
  const locale = useLocale();
  let t0;
  if ($[0] !== locale) {
    t0 = {
      locale
    };
    $[0] = locale;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  let t1;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = {
      selector(param) {
        const projects = param?.data;
        if (!projects) {
          return null;
        }
        const logos = new Set([betterplaceLogo, betterplayLogo]);
        for (const project of projects) {
          const logoUrl = project.carrier?.picture?.links[0]?.href;
          if (!logoUrl || logoUrl.includes("/assets/default")) {
            continue;
          }
          logos.add(logoUrl.replace("100x100", "400x400"));
        }
        return [...logos.values()];
      },
      intervalMs: 5000
    };
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  return useApiV4WithDemoData(`/fundraising_events/${fundraisingEventId}/featured_projects`, t0, t1, demoData);
}
function getPositionClassName(textAlign?: WidgetAlignment): string | undefined {
  switch (textAlign) {
    case 'left':
      return styles.left;
    case 'right':
      return styles.right;
    default:
      return;
  }
}
function ProjectLogosContent(t0) {
  const $ = _c(10);
  const {
    fundraisingEventId,
    demoData,
    withDemoData,
    duration,
    textAlign
  } = t0;
  const {
    data: logos
  } = useData(fundraisingEventId, withDemoData ? demoData : undefined);
  const logosLength = logos?.length ?? 0;
  const [currentIndex_, setCurrentIndex] = useState(0);
  const currentIndex = currentIndex_ < logosLength ? currentIndex_ : logosLength - 1;
  let t1;
  if ($[0] !== logosLength) {
    t1 = () => {
      setCurrentIndex(prevIndex => prevIndex + 1 < logosLength ? prevIndex + 1 : 0);
    };
    $[0] = logosLength;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const iterate = t1;
  const t2 = duration * 1000;
  let t3;
  if ($[2] !== iterate || $[3] !== t2) {
    t3 = {
      callback: iterate,
      intervalMs: t2
    };
    $[2] = iterate;
    $[3] = t2;
    $[4] = t3;
  } else {
    t3 = $[4];
  }
  useInterval(t3);
  if (!logos?.length) {
    return null;
  }
  const src = logos[currentIndex];
  if (!src) {
    return null;
  }
  let t4;
  if ($[5] !== textAlign) {
    t4 = classNames("streambot-logo", getPositionClassName(textAlign));
    $[5] = textAlign;
    $[6] = t4;
  } else {
    t4 = $[6];
  }
  let t5;
  if ($[7] !== src || $[8] !== t4) {
    t5 = <Image src={src} alt="logo" className={t4} onError={console.error} fill={true} />;
    $[7] = src;
    $[8] = t4;
    $[9] = t5;
  } else {
    t5 = $[9];
  }
  return t5;
}
function ProjectLogos(t0) {
  const $ = _c(20);
  let fundraisingEventId;
  let props;
  let t1;
  let t2;
  if ($[0] !== t0) {
    ({
      fundraisingEventId,
      withDemoData: t1,
      demoData: t2,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = fundraisingEventId;
    $[2] = props;
    $[3] = t1;
    $[4] = t2;
  } else {
    fundraisingEventId = $[1];
    props = $[2];
    t1 = $[3];
    t2 = $[4];
  }
  const withDemoData = t1 === undefined ? false : t1;
  const demoData = t2 === undefined ? defaultDemoData : t2;
  let t3;
  if ($[5] !== props) {
    t3 = getDefaultValues(props);
    $[5] = props;
    $[6] = t3;
  } else {
    t3 = $[6];
  }
  const mergedProps = t3;
  let duration;
  let textAlign;
  let wrapperProps;
  if ($[7] !== mergedProps) {
    const {
      duration: t4,
      textAlign: t5,
      darkMode: _,
      ...t6
    } = mergedProps;
    duration = t4;
    textAlign = t5;
    wrapperProps = t6;
    $[7] = mergedProps;
    $[8] = duration;
    $[9] = textAlign;
    $[10] = wrapperProps;
  } else {
    duration = $[8];
    textAlign = $[9];
    wrapperProps = $[10];
  }
  let t4;
  if ($[11] !== demoData || $[12] !== duration || $[13] !== fundraisingEventId || $[14] !== textAlign || $[15] !== withDemoData) {
    t4 = <ProjectLogosContent fundraisingEventId={fundraisingEventId} demoData={demoData} withDemoData={withDemoData} duration={duration} textAlign={textAlign} />;
    $[11] = demoData;
    $[12] = duration;
    $[13] = fundraisingEventId;
    $[14] = textAlign;
    $[15] = withDemoData;
    $[16] = t4;
  } else {
    t4 = $[16];
  }
  let t5;
  if ($[17] !== t4 || $[18] !== wrapperProps) {
    t5 = <StreambotWrapper {...wrapperProps}>{t4}</StreambotWrapper>;
    $[17] = t4;
    $[18] = wrapperProps;
    $[19] = t5;
  } else {
    t5 = $[19];
  }
  return t5;
}
export default ProjectLogos;